/**
 * Format a date string or Date object in the admin user's timezone.
 */
export function formatInTimezone(date: string | Date, timezone: string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === "string" ? new Date(date) : date
  try {
    return d.toLocaleString(undefined, { timeZone: timezone, ...options })
  } catch {
    return d.toLocaleString(undefined, { timeZone: UTC_TIMEZONE, ...options })
  }
}

/**
 * Format a date as a short date string (e.g., "Jan 1, 2025") in the admin timezone.
 */
export function formatDateInTimezone(date: string | Date, timezone: string): string {
  return formatInTimezone(date, timezone, {
    year: "numeric",
    month: "short",
    day: "numeric"
  })
}

/**
 * Format a date as date + time (e.g., "Jan 1, 2025, 14:30:00") in the admin timezone.
 */
export function formatDateTimeInTimezone(date: string | Date, timezone: string): string {
  return formatInTimezone(date, timezone, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  })
}

export function getUtcOffset(tz: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset"
    })
    const parts = formatter.formatToParts(now)
    const offsetPart = parts.find((p) => p.type === "timeZoneName")
    if (offsetPart) {
      return offsetPart.value.replace("GMT", "UTC")
    }
  } catch {
    // ignore
  }
  return ""
}

/** The app's default zone, and the one `Intl.supportedValuesOf` leaves out. See below. */
export const UTC_TIMEZONE = "UTC"

export function isValidTimezone(tz: string): boolean {
  if (!tz) return false
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export function getAllTimezones(): string[] {
  if (typeof Intl !== "undefined" && "supportedValuesOf" in Intl) {
    try {
      const zones = (Intl as any).supportedValuesOf("timeZone") as string[]
      // `supportedValuesOf` answers with CANONICAL IANA zones only, and "UTC" is a Link — so it
      // is absent from a list of ~418, while being both this app's default (see
      // getterAdminTimezone) and a perfectly valid `timeZone` for Intl. Without this the picker
      // cannot offer the value the app starts on: an admin who moves off UTC has no row to move
      // back to. Prepended, not appended, to match the COMMON_TIMEZONES fallback below — the two
      // branches of this function must describe the same set.
      return zones.includes(UTC_TIMEZONE) ? zones : [UTC_TIMEZONE, ...zones]
    } catch {
      // fallback
    }
  }
  return COMMON_TIMEZONES
}

const COMMON_TIMEZONES = [
  UTC_TIMEZONE,
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "America/Mexico_City",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Moscow",
  "Europe/Istanbul",
  "Europe/Kiev",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Singapore",
  "Australia/Sydney",
  "Pacific/Auckland"
]
